// ═══════════════════════════════════════════════════════════════
// PASSWORD POLICY — shared client + server validator
// ═══════════════════════════════════════════════════════════════
//
// Design (per NIST 800-63B, 2017+):
//   • Length > complexity. Min 10 for regular users, 12 for tier-3 admins.
//   • NO forced complexity rules (uppercase/digit/symbol). They don't help
//     and lead to "Password1!" patterns.
//   • DO block common passwords + obvious context (employee's own name,
//     email, "marcstone"). That's where breaches actually start.
//   • NO forced periodic rotation. Research shows rotation makes passwords
//     weaker (people increment Spring2025 → Spring2026).
//
// Used by:
//   • src/components/auth/SetInitialPasswordScreen.tsx   (new hire flow)
//   • src/components/auth/ChangePasswordModal.tsx        (self change)
//   • src/components/auth/AdminResetPasswordModal.tsx    (admin set-pw mode)
//   • supabase/functions/.../employee-routes.tsx         (server re-enforce)
// ═══════════════════════════════════════════════════════════════

export const PASSWORD_MIN_LENGTH = 10;
export const PASSWORD_MIN_LENGTH_ADMIN = 12;
export const PASSWORD_HINT = "Tip: three random words like correct-horse-battery is a strong passphrase that's easier to type than a short jumble.";

// Hand-curated top-40 most-breached passwords. Not exhaustive — for
// completeness we could fetch HIBP API, but this catches the cases that
// matter without adding a network dependency on every keystroke.
const COMMON_PASSWORDS: ReadonlySet<string> = new Set([
  "password", "123456", "12345678", "123456789", "qwerty", "abc123",
  "password1", "password123", "letmein", "monkey", "1234567890", "dragon",
  "111111", "baseball", "iloveyou", "trustno1", "sunshine", "master",
  "welcome", "shadow", "ashley", "football", "jesus", "michael",
  "ninja", "mustang", "access", "test", "admin", "administrator",
  "qwerty123", "1q2w3e4r", "asdfgh", "zxcvbn", "passw0rd", "qwertyuiop",
  "welcome1", "welcome123", "changeme", "default",
]);

export interface PasswordPolicyOpts {
  /** Use admin minimum (12) instead of regular (10). */
  isAdmin?: boolean;
  /** Strings the password must not contain (case-insensitive). Pass employee
   *  name parts, email username, company name, etc. */
  blockContaining?: (string | undefined | null)[];
}

export interface PasswordValidation {
  ok: boolean;
  /** First failure reason, suitable for showing inline under the input. */
  error?: string;
}

export function validatePassword(pw: string, opts: PasswordPolicyOpts = {}): PasswordValidation {
  if (typeof pw !== "string") return { ok: false, error: "Password is required." };

  const minLen = opts.isAdmin ? PASSWORD_MIN_LENGTH_ADMIN : PASSWORD_MIN_LENGTH;
  if (pw.length < minLen) {
    return { ok: false, error: `Use at least ${minLen} characters.` };
  }
  if (pw.length > 128) {
    // Match Supabase's upper bound — bcrypt is capped at 72 bytes anyway,
    // longer inputs get silently truncated which is confusing for users.
    return { ok: false, error: "Maximum 128 characters." };
  }

  const lower = pw.toLowerCase();
  if (COMMON_PASSWORDS.has(lower)) {
    return { ok: false, error: "That's one of the most-breached passwords on the internet. Pick something else." };
  }

  // Block obvious context: name parts, email username, "marcstone".
  // Only block 4+ char fragments to avoid false positives on initials.
  const blockedFragments: string[] = ["marcstone"];
  for (const raw of opts.blockContaining ?? []) {
    if (!raw) continue;
    const cleaned = raw.toLowerCase().trim();
    if (cleaned.length < 4) continue;
    blockedFragments.push(cleaned);
    // For emails, also block the username portion alone.
    const at = cleaned.indexOf("@");
    if (at > 3) blockedFragments.push(cleaned.slice(0, at));
    // For multi-word names, block individual words too.
    for (const word of cleaned.split(/[\s.-]+/)) {
      if (word.length >= 4) blockedFragments.push(word);
    }
  }
  for (const frag of blockedFragments) {
    if (lower.includes(frag)) {
      return { ok: false, error: `Don't include "${frag}" — that's too easy to guess.` };
    }
  }

  return { ok: true };
}
